/**
 * Shared showWhen Evaluator, Validator, and Label Formatter
 *
 * Task: T-P5-D4-F2-1
 * Feature: P5-D4-F2 Shared Form Contract Upgrade
 *
 * Pure functions with no framework dependencies — usable in both Node.js and browser.
 * evaluateShowWhen: runtime visibility evaluation (fail-closed on any malformed shape)
 * validateShowWhenConfig: save-time config validation
 * formatShowWhenLabel: human-readable condition label for admin display
 */

import type { ShowWhen, FormField, FieldType, ShowWhenOperator } from './field.schemas.js';

// ============================================================================
// Constants
// ============================================================================

/**
 * Source field types allowed for showWhen references.
 * Only structured-answer types — freeform fields (TEXT, EMAIL, TEXTAREA)
 * produce unpredictable values that make conditions fragile.
 */
export const SHOW_WHEN_ALLOWED_SOURCE_TYPES: readonly FieldType[] = [
  'SELECT', 'RADIO', 'CHECKBOX',
];

/**
 * Operators allowed for multi-select CHECKBOX sources.
 * equals/notEquals compare strings — semantically nonsensical for arrays.
 */
export const SHOW_WHEN_CHECKBOX_ALLOWED_OPERATORS: readonly ShowWhenOperator[] = [
  'contains', 'isNotEmpty',
];

/**
 * Operators allowed for consent CHECKBOX sources (binary, no options).
 * Value is boolean stored as "true"/"false" — only equality checks make sense.
 */
export const SHOW_WHEN_CONSENT_ALLOWED_OPERATORS: readonly ShowWhenOperator[] = [
  'equals', 'notEquals',
];

// ============================================================================
// evaluateShowWhen
// ============================================================================

/**
 * Evaluate whether a field with a showWhen condition should be visible.
 * Returns true if the field should be shown.
 *
 * Fail-closed: returns false for any malformed/unexpected shape.
 *
 * @param condition - The showWhen rule to evaluate
 * @param sourceValue - The current value of the referenced source field
 * @param sourceField - The source field definition (needed for checkbox mode detection + defensive guards)
 */
export function evaluateShowWhen(
  condition: ShowWhen,
  sourceValue: unknown,
  sourceField: FormField,
): boolean {
  // Defensive guard: unsupported source field types → hidden
  if (!SHOW_WHEN_ALLOWED_SOURCE_TYPES.includes(sourceField.type)) {
    return false;
  }

  // Undefined/null source → hidden for ALL operators (including notEquals)
  if (sourceValue === undefined || sourceValue === null) {
    return false;
  }

  const isArray = Array.isArray(sourceValue);
  const isString = typeof sourceValue === 'string';
  const isBoolean = typeof sourceValue === 'boolean';

  // Empty string source → hidden (no answer = no branching trigger)
  if (isString && sourceValue === '') {
    return false;
  }

  switch (condition.operator) {
    case 'equals': {
      // Array sources with equals → false (semantically nonsensical)
      if (isArray) return false;
      // Boolean source (consent checkbox) → compare as string
      if (isBoolean) return String(sourceValue) === condition.value;
      // Non-string source → fail closed
      if (!isString) return false;
      return sourceValue === condition.value;
    }

    case 'notEquals': {
      // Array sources with notEquals → false (semantically nonsensical)
      if (isArray) return false;
      // Boolean source (consent checkbox) → compare as string
      if (isBoolean) return String(sourceValue) !== condition.value;
      // Non-string source → fail closed
      if (!isString) return false;
      return sourceValue !== condition.value;
    }

    case 'contains': {
      if (isArray) {
        // Checkbox multi-select: membership check
        if (condition.value === undefined) return false;
        return sourceValue.includes(condition.value);
      }
      if (isString) {
        // String: substring check
        return sourceValue.includes(condition.value ?? '');
      }
      // Non-string/non-array → fail closed
      return false;
    }

    case 'isNotEmpty': {
      if (isArray) {
        return sourceValue.length > 0;
      }
      if (isString) {
        return sourceValue.trim() !== '';
      }
      // Boolean/number/other → fail closed
      return false;
    }

    default:
      return false;
  }
}

// ============================================================================
// validateShowWhenConfig
// ============================================================================

/**
 * Validate a showWhen configuration against the form's field list.
 * Returns null if valid, or an error message string if invalid.
 *
 * Rules:
 * 1. fieldId must reference a field that exists in the form
 * 2. Referenced field must appear BEFORE the conditional field in field order
 * 3. Referenced field must be an allowed source type (SELECT, RADIO, CHECKBOX)
 * 4. Consent CHECKBOX (no options): operator must be equals or notEquals
 * 5. Multi-select CHECKBOX (with options): operator must be contains or isNotEmpty
 * 6. 'equals', 'notEquals', 'contains' require value to be present
 * 7. 'isNotEmpty' must NOT have a value
 */
export function validateShowWhenConfig(
  showWhen: ShowWhen,
  fieldIndex: number,
  fields: FormField[],
): string | null {
  // Rule 1: fieldId must reference an existing field
  const sourceIndex = fields.findIndex((f) => f.fieldId === showWhen.fieldId);
  if (sourceIndex === -1) {
    return `showWhen references non-existent field: "${showWhen.fieldId}"`;
  }

  // Rule 2: Referenced field must appear before current field
  if (sourceIndex >= fieldIndex) {
    return `showWhen references field "${showWhen.fieldId}" which is not before the current field (index ${fieldIndex})`;
  }

  const sourceField = fields[sourceIndex];

  // Rule 3: Source type must be allowed
  if (!SHOW_WHEN_ALLOWED_SOURCE_TYPES.includes(sourceField.type)) {
    return `showWhen references field "${showWhen.fieldId}" of unsupported type "${sourceField.type}". Allowed: ${SHOW_WHEN_ALLOWED_SOURCE_TYPES.join(', ')}`;
  }

  // Rule 4: Consent CHECKBOX (no options) — operator must be equals or notEquals
  if (sourceField.type === 'CHECKBOX' && !sourceField.options?.length) {
    if (!SHOW_WHEN_CONSENT_ALLOWED_OPERATORS.includes(showWhen.operator)) {
      return `showWhen uses operator "${showWhen.operator}" with consent CHECKBOX "${showWhen.fieldId}". Only ${SHOW_WHEN_CONSENT_ALLOWED_OPERATORS.join(', ')} are allowed for consent CHECKBOX`;
    }
  }

  // Rule 5: CHECKBOX-with-options — operator restricted to contains/isNotEmpty
  if (
    sourceField.type === 'CHECKBOX' &&
    sourceField.options?.length &&
    !SHOW_WHEN_CHECKBOX_ALLOWED_OPERATORS.includes(showWhen.operator)
  ) {
    return `showWhen uses operator "${showWhen.operator}" with multi-select CHECKBOX "${showWhen.fieldId}". Only ${SHOW_WHEN_CHECKBOX_ALLOWED_OPERATORS.join(', ')} are allowed for multi-select CHECKBOX`;
  }

  // Rule 6: equals, notEquals, contains require value
  if (
    (showWhen.operator === 'equals' || showWhen.operator === 'notEquals' || showWhen.operator === 'contains') &&
    (showWhen.value === undefined || showWhen.value === null)
  ) {
    return `showWhen operator "${showWhen.operator}" requires a value`;
  }

  // Rule 7: isNotEmpty must NOT have a value
  if (showWhen.operator === 'isNotEmpty' && showWhen.value !== undefined) {
    return `showWhen operator "isNotEmpty" must not have a value`;
  }

  return null;
}

// ============================================================================
// formatShowWhenLabel
// ============================================================================

/**
 * Build a human-readable condition label for admin display.
 *
 * Resolves source field labels, option labels (not raw values),
 * and consent checkbox booleans ("true"→"Checked", "false"→"Unchecked").
 *
 * Used by FormPreviewRenderer and FormSubmissionViewer.
 */
export function formatShowWhenLabel(
  showWhen: ShowWhen,
  fields: FormField[],
): string {
  const { fieldId, operator, value } = showWhen;
  const sourceField = fields.find((f) => f.fieldId === fieldId);
  const sourceLabel = sourceField?.label ?? fieldId;

  if (operator === 'isNotEmpty') {
    return `\u21B3 Shown when "${sourceLabel}" has a value`;
  }

  // Resolve display value
  let displayValue = value ?? '';

  if (sourceField) {
    // Consent checkbox: "true"→"Checked", "false"→"Unchecked"
    if (sourceField.type === 'CHECKBOX' && !sourceField.options?.length) {
      if (value === 'true') displayValue = 'Checked';
      else if (value === 'false') displayValue = 'Unchecked';
    }

    // SELECT/RADIO/multi-select CHECKBOX: resolve option label from value
    if (sourceField.options?.length && value) {
      const matchedOption = sourceField.options.find((o) => o.value === value);
      if (matchedOption) displayValue = matchedOption.label;
    }
  }

  if (operator === 'equals') {
    return `\u21B3 Shown when "${sourceLabel}" is ${displayValue}`;
  }

  if (operator === 'notEquals') {
    return `\u21B3 Shown when "${sourceLabel}" is not ${displayValue}`;
  }

  if (operator === 'contains') {
    return `\u21B3 Shown when "${sourceLabel}" includes ${displayValue}`;
  }

  return `\u21B3 Shown when "${sourceLabel}" ${operator} ${displayValue}`;
}

// ============================================================================
// Tag Rule Label Formatter
// ============================================================================

/**
 * Format a tag rule into a human-readable label.
 * Resolves option value to its display label from the field's options.
 *
 * Used by both form detail (frontend) and tag detail (backend) to avoid drift.
 *
 * @param rule - The tag rule to format
 * @param field - The field the rule belongs to (for label + option resolution)
 * @returns e.g. 'When "Experience Level" = "Advanced"'
 */
export function formatTagRuleLabel(
  rule: { optionValue: string },
  field: { label: string; options?: Array<{ label: string; value: string }> },
): string {
  const optionLabel = field.options?.find((o) => o.value === rule.optionValue)?.label ?? rule.optionValue;
  return `When "${field.label}" = "${optionLabel}"`;
}
